import { useEffect, useEffectEvent, useState } from 'react'
import { inkFor } from '../../lib/ink'

const COLORS = ['#1e40af', '#cc470b', '#0a2240', '#a63a09']
const SPIN_MS = 4200
const TURNS = 6

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

const point = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180
  return `${(Math.sin(a) * r).toFixed(2)} ${(-Math.cos(a) * r).toFixed(2)}`
}

/**
 * The roulette wheel. It lands on a name the server already chose; the spin is
 * presentation over a fixed result. Each spin bumps `spin`, and `onLanded`
 * fires once the wheel stops. With reduced motion it lands without turning.
 *
 * Name slices are dark fields with white text (royal 8.72:1, navy 15.96:1,
 * accent-600 4.69:1, accent-700 6.11:1). Color slices take black or white
 * text, whichever contrasts more with the fill.
 */
export default function Wheel({
  names,
  labels,
  fills,
  target,
  spin,
  onLanded,
}: {
  /** Slice identities; the target is one of these. */
  names: string[]
  /** What each slice shows. Defaults to the identity itself. */
  labels?: string[]
  /** Slice colors, one per name. Defaults to the site palette. */
  fills?: string[]
  target: string | null
  spin: number
  onLanded: () => void
}) {
  const [rotation, setRotation] = useState(0)
  const [animate, setAnimate] = useState(false)
  const [seenSpin, setSeenSpin] = useState(0)

  // A new spin sets where the wheel stops: several full turns, then the
  // target slice's centre under the pointer at the top.
  if (spin !== seenSpin) {
    setSeenSpin(spin)
    const index = target ? names.indexOf(target) : -1
    if (spin && index >= 0) {
      const centre = (index + 0.5) * (360 / names.length)
      const reduce = prefersReducedMotion()
      setAnimate(!reduce)
      setRotation(reduce ? -centre : rotation + TURNS * 360 + ((((-centre - rotation) % 360) + 360) % 360))
    }
  }

  const fireLanded = useEffectEvent(() => onLanded())
  useEffect(() => {
    if (!spin) return
    const done = setTimeout(fireLanded, prefersReducedMotion() ? 0 : SPIN_MS + 150)
    return () => clearTimeout(done)
  }, [spin])

  const slice = 360 / Math.max(names.length, 1)

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[34rem]" aria-hidden="true">
      <div className="absolute top-[-6px] left-1/2 z-10 -translate-x-1/2">
        <svg width="44" height="40" viewBox="0 0 44 40">
          <path d="M22 40 2 4h40z" fill="#fb641f" stroke="#ffffff" strokeWidth="3" />
        </svg>
      </div>
      <svg viewBox="-104 -104 208 208" className="h-full w-full drop-shadow-lg">
        <g
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: animate ? `transform ${SPIN_MS}ms cubic-bezier(0.15, 0.85, 0.2, 1)` : 'none',
          }}
        >
          {names.length === 1 ? (
            <circle r="100" fill={fills?.[0] ?? COLORS[0]} />
          ) : (
            names.map((name, i) => {
              const a0 = i * slice
              const a1 = (i + 1) * slice
              const large = slice > 180 ? 1 : 0
              return (
                <path
                  key={name}
                  d={`M0 0 L${point(a0, 100)} A100 100 0 ${large} 1 ${point(a1, 100)} Z`}
                  fill={fills?.[i] ?? COLORS[i % COLORS.length]}
                  stroke="#ffffff"
                  strokeWidth="0.8"
                />
              )
            })
          )}
          {names.map((name, i) => {
            const centre = (i + 0.5) * slice
            const shown = labels?.[i] ?? name
            const label = fills ? shown : shown.split(' ')[0]
            // Labels that will rest on the left half turn the other way, so none
            // read upside down once the wheel stops.
            const resting = (((centre + rotation) % 360) + 360) % 360
            const turn = resting > 180 ? 90 : -90
            return (
              <g key={name} transform={`rotate(${centre})`}>
                <text
                  x="0"
                  y="-58"
                  transform={`rotate(${turn} 0 -58)`}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill={fills?.[i] ? inkFor(fills[i]) : '#ffffff'}
                  fontSize={names.length > 16 ? 8 : 10}
                  fontWeight="800"
                >
                  {label}
                </text>
              </g>
            )
          })}
        </g>
        <circle r="16" fill="#ffffff" />
        <circle r="11" fill="#1e40af" />
      </svg>
    </div>
  )
}
